import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ListingTable } from "components/listing";
import { KeyBlock } from "components/key";
import { ShowHide } from "components/layout";
import { NotificationInline } from "components/notifications";
import { FormDatePeriodSelector } from "components/form-fields";
import { Tooltip } from "components/tooltip";
import { Button } from "components/buttons";
import { ErAlerts } from "./ErAlerts";
import { ErAlertRulesModal } from "./ErAlertRulesModal";
import { ErKeyModal } from "./ErKeyModal";
import { ErPeriodHeader } from "./ErPeriodHeader";
import { ErSettingsModal } from "./ErSettingsModal";
import { SetEarnedRecognitionPeriodOrder, SetEarnedRecognitionHideAssessmentLines } from "state/actions";
import { EarnedRecognitionService } from "services/EarnedRecognitionService";
import classNames from "classnames";
import moment from "moment";
import getIsoPeriodStart from "utils/getIsoPeriodStart";
import { buildKpiPeriodWindow, kpiWindowPeriodsToShow, resolveSelectorMinDate, resolveCalendarMinDate, isPeriodBeforeDate } from "./kpiPeriodWindow";
import { buildKpiColumnClasses, hasCappedEndedPeriod } from "./kpiPeriodShading";
import { buildDvsaSubmissionMessage } from "./erDvsaSubmission";
import { COUNT_KPI_KEY_TO_CODE } from "./kpiCodes";
import "./EarnedRecognition.css";

const emptyColours = [];
const emptyThresholds = [];

// API/anchor dates are exchanged in ISO date format.
const apiDateFormat = "YYYY-MM-DD";
// The selector displays human-readable dates.
const selectorDateFormat = "DD MMM YYYY";
// The window spans 15 four-week ISO periods: the anchor period plus the 14 preceding.
const periodsToShow = kpiWindowPeriodsToShow;

// Explains the KPI/period column layout; shown as a tooltip on the first column's header.
const kpiColumnHeaderTooltip = "Each column is an ISO period. The number is the period; the line beneath is its ISO week range. Hover a period for its calendar dates.";
// Names the KPI stream in the DVSA "not submitting" status line.
const dvsaStreamLabel = "Drivers";
// The DVSA small-operator provision on the drivers KPI table keys on reported tachograph days.
const cappedMarkerTooltip = "Small operator period - fewer than 25 tachograph days reported";

export function DriverHoursKpiSummary() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    // null = the default window (current ISO period back 14). Local state only — deliberately not
    // persisted to Redux (per AC), so leaving and returning to the tab resets the selection.
    const [selectedStartDate, setSelectedStartDate] = useState(null);
    // The alert selected via its eye icon — drives the KPI cell highlight and the banner. Cleared
    // when the window is re-fetched (navigating could move the alert's breach cells out of view).
    const [selectedAlert, setSelectedAlert] = useState(null);
    const [showKeyModal, setShowKeyModal] = useState(false);
    const [showAlertRulesModal, setShowAlertRulesModal] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [breachThresholds, setBreachThresholds] = useState(emptyThresholds);
    const [thresholdsLoading, setThresholdsLoading] = useState(true);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const newestPeriodFirst = useSelector((state) => state.earnedRecognition.newestPeriodFirst);
    // Stored as "hide" so both display settings default to off; the grid and key take the
    // positive form, so the preference is inverted here.
    const hideAssessmentLines = useSelector((state) => state.earnedRecognition.hideAssessmentLines);
    const showAssessmentLines = !hideAssessmentLines;

    useEffect(() => {
        // Re-fetches whenever the selected anchor changes. The API resolves startDate to its ISO
        // period and returns that period back 14, so navigating the selector moves the window.
        setLoading(true);
        setHasError(false);
        setSelectedAlert(null);
        EarnedRecognitionService.getDriverHoursKpis(selectedStartDate).then(
            (response) => {
                setData(response);
                setLoading(false);
            },
            () => {
                setHasError(true);
                setLoading(false);
            }
        );
    }, [selectedStartDate]);

    useEffect(() => {
        // The seeded trigger bands are the single source for the Key modal's breach descriptions and
        // swatch colours. Failure is non-fatal: the modal drops its Breach Types section and the grid
        // still renders. The response is read defensively because a throw here would land in the
        // success handler, where the rejection handler beside it cannot catch it - leaving the section
        // on its loading skeleton indefinitely rather than dropping it.
        EarnedRecognitionService.getKpiThresholds().then(
            (response) => {
                setBreachThresholds(response?.driverHoursThresholds ?? emptyThresholds);
                setThresholdsLoading(false);
            },
            () => {
                setBreachThresholds(emptyThresholds);
                setThresholdsLoading(false);
            }
        );
    }, []);

    // The selector is anchored at its most-recent period and extends back (periodsToShow - 1) periods,
    // so the anchor for the default window is the CURRENT ISO period. Reset returns to this anchor,
    // which clears selectedStartDate back to null.
    const defaultWindowAnchor = getIsoPeriodStart(moment()).format(selectorDateFormat);

    function handlePeriodChange(periodStart, periodEnd) {
        // The anchor sent to the API is the first day of the most-recent ISO period in the window,
        // i.e. the period containing periodEnd. When that equals the default window's anchor we send
        // null so the API falls back to its default (current period back 14).
        const anchor = getIsoPeriodStart(moment(periodEnd, selectorDateFormat));
        const defaultAnchor = getIsoPeriodStart(moment());

        if (anchor.isSame(defaultAnchor, "day")) {
            setSelectedStartDate(null);
            return;
        }

        setSelectedStartDate(anchor.format(apiDateFormat));
    }

    function handleSelectAlert(alert) {
        // Toggle: clicking the eye on the already-selected alert clears the highlight.
        setSelectedAlert((current) => {
            if (current != null && current.id === alert.id) {
                return null;
            }
            return alert;
        });
    }

    function clearSelectedAlert() {
        setSelectedAlert(null);
    }

    function openKeyModal() {
        setShowKeyModal(true);
    }

    function closeKeyModal() {
        setShowKeyModal(false);
    }

    function openAlertRulesModal() {
        setShowAlertRulesModal(true);
    }

    function closeAlertRulesModal() {
        setShowAlertRulesModal(false);
    }

    function openSettingsModal() {
        setShowSettingsModal(true);
    }

    function closeSettingsModal() {
        setShowSettingsModal(false);
    }

    function handlePeriodOrderChange(showNewestFirst) {
        dispatch(SetEarnedRecognitionPeriodOrder(showNewestFirst));
    }

    function handleHideAssessmentLinesChange(hideLines) {
        dispatch(SetEarnedRecognitionHideAssessmentLines(hideLines));
    }

    // When an alert is selected (via its eye icon), highlight only the KPI cells that caused it —
    // keyed by ISO year/period and KPI label from the alert's breaches — and dim every other KPI cell.
    const selectedBreachKeys = selectedAlert != null
        ? new Set(selectedAlert.breaches.map((breach) => `${breach.isoYear}-${breach.isoPeriod}-${breach.kpiLabel}`))
        : null;

    // Finds the value for a KPI series that matches a given period, or null when none exists.
    function findValueForPeriod(values, period) {
        if (values == null) {
            return null;
        }

        return values.find(
            (value) => value.isoYear === period.isoYear && value.isoPeriod === period.isoPeriod
        );
    }

    // Resolves the KeyBlock colours for a KPI cell. A triggered value normally fills the whole
    // block; in an open period it is shown as a bottom-left triangle instead. KeyBlock renders the
    // fill via its four border colours (top, right, bottom, left), so colouring bottom and left and
    // leaving top and right white produces a triangle in the bottom-left half.
    function resolveKeyBlockColours(value, period) {
        if (!value || !value.triggerColourHex) {
            return emptyColours;
        }

        if (period.isOpen) {
            return ["#FFFFFF", "#FFFFFF", value.triggerColourHex, value.triggerColourHex];
        }

        return value.triggerColourHex;
    }

    function buildKpiRow(kpi, periods) {
        const labelCell = (
            <div className="er-kpi-label">
                <div className="er-kpi-name">{kpi.label}</div>
                <div className="er-kpi-threshold">{`Threshold breach: ${kpi.thresholdDescription}`}</div>
            </div>
        );

        const periodCells = periods.map((period) => {
            const value = findValueForPeriod(kpi.values, period);
            const colours = resolveKeyBlockColours(value, period);
            const percentageText = value && value.percentage != null ? `${value.percentage}%` : "-";
            const dimmed = selectedBreachKeys != null && !selectedBreachKeys.has(`${period.isoYear}-${period.isoPeriod}-${kpi.label}`);
            // Periods before the account KPI start floor have no drilldown, so their cell is inert:
            // KeyBlock keeps its container-level onClick and clickable styling only when selectable,
            // so gate both to leave a pre-floor block unstyled-as-clickable and unresponsive.
            const isClickable = !period.isBeforeFloor;

            return (
                <div key={`${period.isoYear}-${period.isoPeriod}`} className={classNames("er-kpi-cell", { "er-kpi-cell-dimmed": dimmed })}>
                    <KeyBlock
                        colours={colours}
                        selectable={isClickable}
                        onClick={isClickable ? () => navigate(`/earned-recognition/driver-kpi?isoYear=${period.isoYear}&isoPeriod=${period.isoPeriod}&kpi=${kpi.key}`) : undefined}
                    />
                    <div className="er-kpi-percentage">{percentageText}</div>
                </div>
            );
        });

        return [labelCell, ...periodCells];
    }

    function buildCountRow(countSeries, periods) {
        const labelCell = (
            <span className="er-kpi-count-label">{countSeries.label}</span>
        );

        // The count KPIs are keyed by name on the dashboard but drilled into by D-code; resolve the
        // D-code once for the whole row. A count with no mapped D-code is left non-clickable (defensive).
        const kpiCode = COUNT_KPI_KEY_TO_CODE[countSeries.key];

        const periodCells = periods.map((period) => {
            const value = findValueForPeriod(countSeries.values, period);
            const hasCount = value && value.count != null;
            const countText = hasCount ? `${value.count}` : "-";
            // A zero count is a good result, so only a count of one or more takes the flagged styling.
            // An absent value still reads as "-" rather than a zero.
            const isHighlighted = hasCount && value.count > 0;

            // Each count cell deep-links into the drilldown for its ISO year/period and the count's
            // D-code — clickable regardless of whether the period has a value, mirroring how the KPI
            // KeyBlocks are navigable. A native button keeps the cell keyboard-accessible. Periods
            // before the account KPI start floor have no drilldown, so they fall through to the plain
            // <span> branch below.
            if (kpiCode != null && !period.isBeforeFloor) {
                return (
                    <button
                        type="button"
                        key={`${period.isoYear}-${period.isoPeriod}`}
                        className={classNames("er-kpi-count-link", { "er-kpi-count-value": isHighlighted })}
                        onClick={() => navigate(`/earned-recognition/driver-kpi?isoYear=${period.isoYear}&isoPeriod=${period.isoPeriod}&kpi=${kpiCode}`)}
                    >
                        {countText}
                    </button>
                );
            }

            return (
                <span
                    key={`${period.isoYear}-${period.isoPeriod}`}
                    className={classNames({ "er-kpi-count-value": isHighlighted })}
                >
                    {countText}
                </span>
            );
        });

        return [labelCell, ...periodCells];
    }

    let headers = [];
    let rows = [];
    let rowClasses = [];
    let columnClasses = [];
    let hasCappedPeriod = false;
    let hasPreFloorPeriod = false;
    let hasPreSchemePeriod = false;

    // The date the operator told us they joined the DVSA scheme. It drives the rules modal's wording
    // only — never the grid's own floor, so periods before it stay visible and navigable.
    const schemeStartDate = data?.dvsaSubmission?.schemeStartDate ?? null;

    if (data != null) {
        // The API clips its periods to the account's KPI start floor; pad back to the full 15-period
        // window. Padded periods carry no values, so their cells render like any no-data cell.
        const windowAnchor = selectedStartDate != null ? moment(selectedStartDate, apiDateFormat) : moment();
        const periods = buildKpiPeriodWindow(data.periods, windowAnchor, data.kpiStartFloor, newestPeriodFirst);

        const kpiColumnHeader = (
            <Tooltip key="kpi-column-header" content={kpiColumnHeaderTooltip}>
                <span className="er-kpi-header">KPI / ISO Week / Period</span>
            </Tooltip>
        );
        hasCappedPeriod = hasCappedEndedPeriod(periods);
        hasPreFloorPeriod = periods.some((period) => period.isBeforeFloor);
        hasPreSchemePeriod = periods.some(
            (period) => isPeriodBeforeDate(period.isoYear, period.isoPeriod, schemeStartDate)
        );
        headers = [kpiColumnHeader, ...periods.map((period) => (
            <ErPeriodHeader
                key={`${period.isoYear}-${period.isoPeriod}`}
                period={period}
                showCappedSlot={hasCappedPeriod}
                cappedMarkerTooltip={cappedMarkerTooltip}
            />
        ))];

        const kpiRows = data.kpis.map((kpi) => buildKpiRow(kpi, periods));
        const countRows = data.counts.map((countSeries) => buildCountRow(countSeries, periods));

        rows = [...kpiRows, ...countRows];

        // Count rows get their own class (for extra vertical spacing); the first
        // count row additionally carries the heavier divider border above it.
        const firstCountRowIndex = kpiRows.length;
        rowClasses = rows.map((row, index) => {
            const classes = [];
            if (index >= firstCountRowIndex) {
                classes.push("er-kpi-count-row");
            }
            if (index === firstCountRowIndex) {
                classes.push("er-kpi-section-divider");
            }
            return classes.join(" ");
        });

        columnClasses = buildKpiColumnClasses(periods, data.countingWindow, data.alerts, showAssessmentLines);
    } else {
        // While loading there is no data yet, so render placeholder column headers (the KPI label
        // column plus periodsToShow period columns). Without headers the ListingTable skeleton has
        // no columns to render, so the loading effect would not appear on the initial load.
        const kpiColumnHeader = (
            <Tooltip key="kpi-column-header" content={kpiColumnHeaderTooltip}>
                <span className="er-kpi-header">KPI / ISO Week / Period</span>
            </Tooltip>
        );
        const placeholderHeaders = [];
        for (let index = 0; index < periodsToShow; index++) {
            placeholderHeaders.push(<div className="er-kpi-period-header" key={`placeholder-${index}`} />);
        }
        headers = [kpiColumnHeader, ...placeholderHeaders];
    }

    // The DVSA submission status line: only shown once the dashboard has loaded and the response
    // carries a dvsaSubmission block whose state maps to a message.
    const dvsaMessage = data != null ? buildDvsaSubmissionMessage(data.dvsaSubmission, dvsaStreamLabel) : null;

    return (
        <React.Fragment>
            <p className="intro-text er-intro">
                The Earned Recognition scheme assesses operator compliance against a set of Key Performance Indicators. This page shows operator performance for each KPI over the selected reporting period.
            </p>
            <div className="er-dvsa-notification">
                <NotificationInline
                    show={dvsaMessage != null}
                    intent="info"
                    allowClose={false}
                    text={dvsaMessage != null ? dvsaMessage : ""}
                />
            </div>
            <div className="er-kpi-period-selector inline-items spacer-bottom-small">
                <div className="inline-item fill">
                    <div className="inline-items">
                        <FormDatePeriodSelector
                            startDate={defaultWindowAnchor}
                            resetDate={defaultWindowAnchor}
                            periodType="isoPeriod"
                            periodsToShow={periodsToShow}
                            firstDayOfWeek={1}
                            dateFormat={selectorDateFormat}
                            maxDate={moment().toDate()}
                            minDate={data != null ? resolveSelectorMinDate(data.kpiStartFloor) : undefined}
                            calendarMinDate={data != null ? resolveCalendarMinDate(data.kpiStartFloor) : undefined}
                            showText={false}
                            showCalendar={true}
                            showResetButton={true}
                            onChange={handlePeriodChange}
                        />
                        <div className="inline-divide"></div>
                        <div className="button-row">
                            <Tooltip content="Key">
                                <Button intent="secondary" minimal={true} large={false} icon="properties" iconOnly id="er-kpi-key" onClick={openKeyModal} />
                            </Tooltip>
                            <Tooltip content="What raises an alert">
                                <Button intent="secondary" minimal={true} large={false} icon="info-sign" iconOnly id="er-kpi-alert-rules" onClick={openAlertRulesModal} />
                            </Tooltip>
                        </div>
                        <div className="inline-divide"></div>
                        <div className="button-row">
                            <Button text="Settings" minimal={true} large={false} intent="secondary" icon="cog" id="er-kpi-settings" className="configure-prefs-button" onClick={openSettingsModal} />
                        </div>
                    </div>
                </div>
            </div>
            <ShowHide
                evaluator={hasError}
                show={(<p>Unable to load Drivers Hours KPIs. Please try again later.</p>)}
                hide={(
                    <React.Fragment>
                        <NotificationInline
                            show={selectedAlert != null}
                            intent="info"
                            isBanner={true}
                            allowClose={true}
                            onClose={clearSelectedAlert}
                            text={selectedAlert != null ? `Highlighting the KPI cells for the ISO period ${selectedAlert.isoPeriod} alert — ${selectedAlert.triggerDescription}` : ""}
                        />
                        <ShowHide
                            evaluator={selectedAlert != null}
                            show={(<div className="spacer-bottom"></div>)}
                        />
                        <div className="er-kpi-table">
                            <ListingTable
                                loadingData={loading}
                                data={rows}
                                headers={headers}
                                totalRecordCount={rows.length}
                                rowClasses={rowClasses}
                                columnClasses={columnClasses}
                                pageable={false}
                                sortable={false}
                            />
                        </div>
                        <div className="spacer-bottom"></div>
                        <ErAlerts
                            alerts={data ? data.alerts : []}
                            loading={loading}
                            onSelectAlert={handleSelectAlert}
                            selectedAlertId={selectedAlert != null ? selectedAlert.id : null}
                            stream="Drivers"
                        />
                    </React.Fragment>
                )}
            />
            <ErKeyModal
                isOpen={showKeyModal}
                onClose={closeKeyModal}
                stream="Drivers"
                showSmallOperatorRow={hasCappedPeriod}
                showAssessmentLines={showAssessmentLines}
                thresholds={breachThresholds}
                thresholdsLoading={thresholdsLoading}
            />
            <ErAlertRulesModal
                isOpen={showAlertRulesModal}
                onClose={closeAlertRulesModal}
                stream="Drivers"
                dvsaSubmission={data != null ? data.dvsaSubmission : null}
                showStartDateLine={hasPreFloorPeriod}
                kpiStartFloor={data != null ? data.kpiStartFloor : null}
                schemeStartDate={schemeStartDate}
                showPreSchemeCaveat={hasPreSchemePeriod}
            />
            <ErSettingsModal
                isOpen={showSettingsModal}
                onClose={closeSettingsModal}
                newestPeriodFirst={newestPeriodFirst}
                onPeriodOrderChange={handlePeriodOrderChange}
                hideAssessmentLines={hideAssessmentLines}
                onHideAssessmentLinesChange={handleHideAssessmentLinesChange}
            />
        </React.Fragment>
    );
}
